
    import Vue from "vue";
    import Component from "vue-class-component";
    import { StateChanger } from "vue-infinite-loading";
    import { Watch } from "vue-property-decorator";
    import ApiButton from "@/components/ApiButton.vue";
    import store from "@/store/store";
    import router from "@/router/router";
    import apiClient from "@/stuff/ApiClient";
    import utils from "@/stuff/Utils";
    import { mapData } from "@/stuff/DataMapping";
    import { LookupGroup, TriState } from "@/model/Enums";
    import { SupplierSearchParameters } from "@/model/SupplierSearchParameters";
    import { SiteSearchParameters } from "@/model/SiteSearchParameters";
    import { LookupItem, ILookupItem } from "@/model/LookupItem";
    import { RegionLookupItem } from "@/model/RegionLookupItem";
    import { ISite, Site } from "@/model/Site";
    import { Supplier, ISupplier } from "@/model/Supplier";
    import { User } from "@/model/User";
    import TickOrCross from "@/components/TickOrCross.vue";
    import * as toastr from "toastr";
import { watch } from "fs";

    @Component({
        components: { ApiButton, TickOrCross }
    })
    export default class Suppliers extends Vue {

        constructor() {
            super();
            // setting parameters here to avoid tripping watchers...
            this.searchParameters.isDormant = TriState.False;
        }

        async mounted() {
            await Promise.all([
                this.loadCompanyTypeList(),
                this.loadBuyerList(),
                this.loadSiteList(),
                this.loadRegionList()
            ]);
        }

        //
        // -- properties
        //

        private searchIsBusy: boolean = false;
        searchParameters = new SupplierSearchParameters();
        private totalCount = -1;
        supplierList: Array<Supplier> = [];
        follow = { 
            supplierID: 0, 
            supplierName: "", 
            amFollowing: false 
        };

        companyTypeList: Array<ILookupItem> = [];

        private buyerList: Array<ILookupItem> = [];
        buyerOptions: Array<ILookupItem> = [];

        private regionList: Array<RegionLookupItem> = [];
        get regionOptions(): Array<ILookupItem> { 
            const filtered = this.regionList.filter(lu => lu.id > 0 && !lu.isArchived && lu.buyerID === this.searchParameters.buyerID);
            return filtered.length === 0 
                ? [ new LookupItem( { id: 0, description:  "(No regions for this buyer!)", isArchived: false }) ]
                : [ new LookupItem( { id: 0, description:  "Any region", isArchived: false }), ...filtered];
        }

        private siteList: Array<ISite> = [];
        get siteOptions(): Array<ISite> { 
            const filtered = this.siteList.filter(s => s.id > 0 && !utils.hasDateValue(s.deleted) && s.regionID === this.searchParameters.regionID);
            return filtered.length === 0 
                ? [ Site.emptySiteOption("(No locations for region)") ]
                : [ Site.emptySiteOption("Any location"), ...filtered];
        }

        get isDormantSearchOptions(): Array<ILookupItem> { return [
            new LookupItem({ id: TriState.UseDefault, description: "Any Status", isArchived: false }),
            new LookupItem({ id: TriState.True, description: "Dormant", isArchived: false }),
            new LookupItem({ id: TriState.False, description: "Active", isArchived: false })
        ];}

        get roleOptions(): Array<ILookupItem> { return [
            new LookupItem({ id: 0, description: "Any Role", isArchived: false }),
            new LookupItem({ id: 1, description: "Consultant", isArchived: false }),
            new LookupItem({ id: 2, description: "Contractor", isArchived: false }),
            new LookupItem({ id: 3, description: "Consultant & Contractor", isArchived: false })
        ];}

        get countText(): string {
            return this.totalCount === -1 ? "..." : this.totalCount.toString();
        }

        get selectedRole(): number {
            if (this.searchParameters.isConsultant && this.searchParameters.isContractor) return 3;
            else if (this.searchParameters.isConsultant) return 1;
            else if (this.searchParameters.isContractor) return 2;
            else return 0;
        }
        set selectedRole(value: number) {
            this.searchParameters.isConsultant = value == 1 || value == 3;
            this.searchParameters.isContractor = value == 2 || value == 3;
            this.refreshSearch();
        }

        //
        // -- watchers
        //

        debouncedSearch = utils.debounce(this.refreshSearch, 400)

        @Watch("searchParameters.searchText")
        onSearchTextChanged() {
            this.debouncedSearch();
        }

        @Watch("searchParameters.buyerID")
        onBuyerIdChanged() {
            if (this.searchParameters.regionID === 0) this.refreshSearch();
            else this.searchParameters.regionID = 0;
        }

        @Watch("searchParameters.regionID")
        onRegionIdChanged() {
            if (this.searchParameters.siteID === 0) this.refreshSearch();
            else this.searchParameters.siteID = 0;
        }

        @Watch("searchParameters.siteID")
        onSiteIdChanged() {
            this.refreshSearch();
        }

        @Watch("searchParameters.isDormant")
        onIsDormantChanged() {
            this.refreshSearch();
        }

        @Watch("searchParameters.amFollowing")
        onFollowingChanged() {
            this.refreshSearch();
        }

        //
        // -- methods
        //

        private async loadCompanyTypeList() {
            this.companyTypeList = await apiClient.get(`/api/lookup/search/${LookupGroup.CompanyType}`);
        }

        async loadBuyerList() {
            this.buyerList = await apiClient.get("/api/buyer/lookups?supplierID=0");
            this.buyerOptions = utils.selectOptions(this.buyerList, "Any buyer");
        }

        private async loadRegionList() {
            this.regionList = await apiClient.get("api/region/lookups");
        }

        private async loadSiteList() {
            const parms = new SiteSearchParameters();
            //parms.regionID = this.regionSupplier.regionID;
            // load ALL sites and filter them here (might have to revisit this if there are 1000s of sites)
            this.siteList = await apiClient.post("/api/site/search", parms);
        }

        async edit(supplierData: ISupplier) {
            router.push("/supplier/" + supplierData.id);
        }

        editNew() {
            router.push("/supplier/" + 0);
        }

        showFollowDialogue(supplierData: ISupplier) {
            this.follow.supplierID = supplierData.id;
            this.follow.supplierName = supplierData.name;
            this.follow.amFollowing = supplierData.amFollowing;
            this.$bvModal.show("followDialogue");
        }

        hideFollowDialogue() {
            this.$bvModal.hide("followDialogue");
            utils.resetObject(this.follow);
        }

        async saveFollow(event: Event, isFollow: boolean) {
            await apiClient.get(`/api/supplier/${isFollow ? "follow" : "unfollow"}?supplierID=${this.follow.supplierID}`, event);
            toastr.success( this.follow.supplierName, isFollow ? "Now Following" : "No Longer Following");           
            this.$bvModal.hide("followDialogue");
            this.refreshSearch();
        }


        // There's code in router.ts and store.ts to handle this
        // (this works across tabs becuase login is preserved in browser session storage
        // but storage is COPIED for new tab and only if it's a CHILD tab!)
        // https://developer.mozilla.org/en-US/docs/Web/API/Window/sessionStorage
        goToSupplierView(supplierData: ISupplier) {
            const routeData = this.$router.resolve({ name: "SupplierZoneHome" });

            // update session storage - child tab will inherit this data!
            const user = this.$store.state.signedInUser as User;
            user.supplierID = supplierData.id;
            store.dispatch("setSignedInUser", user);

            // so new tab can get supplier name, etc. without going off to the server
            window.sessionStorage.setItem("supplierData", JSON.stringify(supplierData));
            
            window.open(routeData.href, "_blank");
        }

        resetSearch(event?: Event) {
            utils.resetObject(this.searchParameters);
            this.searchParameters.isDormant = TriState.False;
            this.refreshSearch(event);
        }

        refreshSearch(event?: Event) {
            this.supplierList = [];
            this.searchParameters.pageNumber = 1;
            this.totalCount = -1;
            this.search(event);
        }

        async infiniteLoadingHandler(stateChanger: StateChanger) {
            if (this.supplierList.length >= this.totalCount && this.totalCount > -1) {
                stateChanger.complete();
                return;
            }
            this.searchParameters.pageNumber += 1;
            await this.search();
            if (this.supplierList.length >= this.totalCount) {
                stateChanger.complete();
            }
            else {
                stateChanger.loaded();
            }           
        }

        private async search(event?: Event) {
            if (this.searchIsBusy) {
                console.log("Skipped search because busy...");
                return;
            }
            this.searchIsBusy = true;
            const response = await apiClient.post("/Api/Supplier/Search", this.searchParameters, event);
            if (this.searchParameters.pageNumber === 1) {
                this.totalCount = response.count;
            }
            this.supplierList.push(...mapData<Array<Supplier>>(response.list, { root: (data) => new Supplier(data) }));
            this.searchIsBusy = false;
        }
    }
